/**
 * MyStatementBanner — Canal B Self-Service
 * ----------------------------------------
 * Bandeau "Mon relevé de compte" affiché en haut de la page Finances client.
 * - Affiche le solde à régler + nombre de factures impayées (si > 0)
 * - Aperçu rapide des KPIs synthèse + bouton 1-clic "Télécharger mon relevé (PDF)"
 * - Strictement aligné charte Baobab : vert #16A34A + gris + blanc, jamais de rouge agressif
 * - Masqué automatiquement si totalDue = 0 (zéro état)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileBarChart, Download, ChevronRight, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtAmount = (v) => `${Math.round(Number(v || 0)).toLocaleString('fr-FR')}`;

export const MyStatementBanner = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client/statement/preview`);
      setPreview(res.data);
    } catch (e) {
      // 404 = pas de client/factures, 403 = pas autorisé → on cache simplement le bandeau
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.post(`${API}/client/statement`, {}, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${preview?.statementNumber || 'releve'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: 'Relevé téléchargé',
        description: `Référence : ${preview?.statementNumber || ''}`,
      });
    } catch (e) {
      toast({
        title: 'Erreur',
        description: "Impossible de télécharger le relevé pour le moment.",
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Loading state — placeholder discret
  if (loading) {
    return (
      <Card className="p-4 bg-emerald-50/40 border-emerald-100" data-testid="my-statement-banner-loading">
        <div className="flex items-center gap-3 text-emerald-700/60">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Chargement de votre relevé…</span>
        </div>
      </Card>
    );
  }

  // Aucun preview disponible (pas de factures du tout)
  if (!preview || !preview.summary) return null;

  const { summary, statementNumber, issueDate, aging } = preview;
  const totalDue = Number(summary.totalDue || 0);
  const unpaidCount = summary.unpaidCount || 0;
  const allClear = totalDue <= 0;

  return (
    <>
      <Card
        className={`relative overflow-hidden border ${allClear ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-emerald-300 shadow-sm'}`}
        data-testid="my-statement-banner"
      >
        {/* Accent gauche vert Baobab */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-600" />

        <div className="p-4 sm:p-5 pl-5 sm:pl-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
                <FileBarChart className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900">Mon relevé de compte</h3>
                {allClear ? (
                  <p className="text-sm text-emerald-700 mt-0.5" data-testid="my-statement-allclear">
                    Toutes vos factures sont à jour. Bravo !
                  </p>
                ) : (
                  <p className="text-sm text-slate-600 mt-0.5">
                    <strong className="text-slate-900" data-testid="my-statement-amount">{fmtAmount(totalDue)} FCFA</strong>
                    <span className="text-slate-500"> à régler · {unpaidCount} facture{unpaidCount > 1 ? 's' : ''} en attente</span>
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  Référence&nbsp;: <span className="font-mono">{statementNumber}</span> · au {issueDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {!allClear && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetail(true)}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  data-testid="my-statement-view-detail-btn"
                >
                  Voir le détail
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="my-statement-download-btn"
              >
                {downloading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Télécharger mon relevé</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Modale détail */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg" data-testid="my-statement-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="w-5 h-5 text-emerald-600" />
              Détail de mon relevé
            </DialogTitle>
            <DialogDescription>
              Référence&nbsp;<span className="font-mono">{statementNumber}</span> · émis le {issueDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Hero KPIs */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total facturé</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{fmtAmount(summary.totalInvoiced)} <span className="text-xs font-normal text-slate-500">FCFA</span></p>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <p className="text-xs text-emerald-700 uppercase tracking-wider">Total réglé</p>
                <p className="text-lg font-bold text-emerald-700 mt-1">{fmtAmount(summary.totalPaid)} <span className="text-xs font-normal text-emerald-600">FCFA</span></p>
              </div>
              <div className="col-span-2 rounded-lg bg-white border-2 border-emerald-600 p-3">
                <p className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">Solde à régler</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{fmtAmount(totalDue)} <span className="text-sm font-normal text-emerald-600">FCFA</span></p>
                <p className="text-xs text-slate-500 mt-1">{unpaidCount} facture{unpaidCount > 1 ? 's' : ''} en attente</p>
              </div>
            </div>

            {/* Aging */}
            {Array.isArray(aging) && aging.some(a => a.amount > 0) && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Vieillissement</p>
                <div className="grid grid-cols-4 gap-2">
                  {aging.map((bucket) => (
                    <div key={bucket.label} className="rounded-md bg-slate-50 border border-slate-200 p-2 text-center">
                      <p className="text-[10px] text-slate-500">{bucket.label}</p>
                      <p className="text-sm font-semibold text-slate-900 mt-0.5">{fmtAmount(bucket.amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-500 leading-relaxed">
              Pour toute question relative à ce relevé, écrivez-nous à <a className="text-emerald-700 hover:underline" href="mailto:compta@baobabco.net">compta@baobabco.net</a> en mentionnant la référence ci-dessus.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetail(false)}>
              <X className="w-4 h-4 mr-2" />Fermer
            </Button>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="my-statement-detail-download-btn"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération…</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Télécharger le PDF</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyStatementBanner;
